/* eslint-disable no-unused-expressions */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MontlyRateCalculationPayload,
  MontlyRateCalculationPayloadReduced
} from '../../../store/_declarations/target';
import { montlyRateCalculationApi } from '../../../store/api/financialCalculator';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import Button from '../../atoms/Button';
import Font from '../../atoms/Font';
import Spinner from '../../atoms/Spinner';
import TextField from '../../atoms/TextField';
import {
  ContentWrapper,
  Divider,
  InputWrapper,
  PromoCodeAction,
  PromoCodeContainer,
  Row,
  ValidationError
} from './styles';

export interface PromoCodeProps {}

const PromoCode: React.FC<PromoCodeProps> = () => {
  const { t } = useTranslation();
  const { promoCodeValidation, selectedItem, widgetType } = useAppSelector(
    (state) => state.app
  );
  const { montlyRateCalculationPayloads } = useAppSelector(
    (state) => state.financialCalculator
  );

  const dispatch = useAppDispatch();
  const [promoCode, setPromoCode] = useState('');
  const [validatedCode, setValidatedCode] = useState(false);
  const [validationError, setValidationError] = useState(false);

  const isLoadingApi = useAppSelector((state) =>
    selectedItem
      ? state.financialCalculator.montlyRateCalculation?.[selectedItem] ===
        'loading'
      : false
  );

  const isLoading = isLoadingApi && promoCodeValidation;

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPromoCode(event.target.value);
    setValidationError(false);
    setValidatedCode(false);
  };

  const checkPayloadAndSendRequest = (pc?: string) => {
    const payload: MontlyRateCalculationPayload | undefined = selectedItem
      ? montlyRateCalculationPayloads?.[selectedItem]
      : undefined;
    if (payload) {
      const {
        pricingProcedure,
        widgetSelection: {
          parameters,
          vehicle: { accessory }
        }
      } = payload;
      const newPayload: MontlyRateCalculationPayloadReduced = {
        masterAgreement: parameters.masterAgreement,
        pricingProcedure,
        widgetSelection: {
          parameters,
          vehicle: {
            accessory
          }
        }
      };
      if (pc) {
        newPayload.widgetSelection.context = {
          discountCode: pc
        };
      }
      dispatch(montlyRateCalculationApi(newPayload));
    }
  };

  const handlePromoCodeValidate = () => {
    checkPayloadAndSendRequest(promoCode);
  };

  const handleRemove = () => {
    setPromoCode('');
    setValidatedCode(false);
    setValidationError(false);
    checkPayloadAndSendRequest();
  };

  useEffect(() => {
    const payload: MontlyRateCalculationPayload | undefined = selectedItem
      ? montlyRateCalculationPayloads?.[selectedItem]
      : undefined;
    if (payload?.widgetSelection.context?.discountCode && promoCode) {
      if (payload?.widgetSelection.context?.discountCode === promoCode) {
        setPromoCode(payload?.widgetSelection.context?.discountCode);
        setValidatedCode(true);
      } else {
        setValidationError(true);
      }
    }
  }, [montlyRateCalculationPayloads, selectedItem]);

  return (
    <PromoCodeContainer>
      <Divider />
      <ContentWrapper>
        <Font
          customConfiguration={{ fontSize: '16' }}
          variant={widgetType === 'modal' ? 'bodyBold' : 'bodyBold'}
        >
          {t('PromoCode')}
        </Font>
        <Row>
          <InputWrapper>
            <TextField
              data-testid="promo-code-input"
              disabled={validatedCode}
              id="promoCode"
              onChange={handleInputChange}
              placeholder={promoCode ? '' : t('PromoCodeLabel')}
              rightIconInput={validatedCode ? 'InterfaceCheck' : ''}
              value={promoCode}
            />
          </InputWrapper>

          {validatedCode || validationError ? (
            <PromoCodeAction
              $setMarginBottom
              className="promoCode-action-remove"
              data-testid="promo-code-action"
              tabIndex={0}
            >
              {isLoading ? (
                <Spinner data-testid="promocode-spinner-remove" />
              ) : (
                <Button
                  customConfiguration={{ height: 43 }}
                  iconName="InterfaceTrash"
                  iconPosition="right"
                  onClick={handleRemove}
                  variant="iconLink"
                >
                  {t('Remove')}
                </Button>
              )}
            </PromoCodeAction>
          ) : (
            <PromoCodeAction
              $setMarginBottom={isLoading || false}
              className="promoCode-action"
              data-testid="promo-code-action"
              onClick={handlePromoCodeValidate}
              tabIndex={0}
            >
              {isLoading ? (
                <Spinner data-testid="promocode-spinner-validate" size={24} />
              ) : (
                <Button
                  customConfiguration={{ height: 43 }}
                  data-testid="promocode-button-validate"
                  disabled={!promoCode}
                  theme="light"
                  variant="quaternary"
                >
                  {t('Validate')}
                </Button>
              )}
            </PromoCodeAction>
          )}
        </Row>
        {validationError ? (
          <ValidationError>
            <Font
              customConfiguration={{ color: 'warning' }}
              variant="bodySRegular"
            >
              {t('ValidationError')}
            </Font>
          </ValidationError>
        ) : (
          ''
        )}
      </ContentWrapper>
      <Divider />
    </PromoCodeContainer>
  );
};

export default PromoCode;
